/**
 * Shared helpers for the administration area (config, workflows, reports).
 * Kept inside the feature folder on purpose: the admin area must not touch shared foundation files.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { Icono } from '@xiltrion/ui/icono';
import { avisar } from '@xiltrion/ui/sonner';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { SelectorMultiple } from '@xiltrion/ui/bloques/selectores';
import { Badge } from '@xiltrion/ui/badge';
import { Textarea } from '@xiltrion/ui/textarea';
import { ApiError } from '@/api/client';
import { KeyValue } from '@/components/ui';

/* ---------- Avisos ---------- */
type Severity = 'success' | 'info' | 'warn' | 'error';
export function notify(severity: Severity, summary: string, detail?: string) {
  const opts = { description: detail, duration: severity === 'error' ? 6000 : 3500 };
  if (severity === 'success') avisar.success(summary, opts);
  else if (severity === 'error') avisar.error(summary, opts);
  else if (severity === 'warn') avisar.warning(summary, opts);
  else avisar.info(summary, opts);
}
export function notifyError(e: unknown) {
  notify('error', 'No se pudo completar la acción', errorMessage(e));
}
export function errorMessage(e: unknown, fallback = 'Ocurrió un error inesperado.'): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

/* ---------- Confirmación ---------- */
interface ConfirmOpts { message: ReactNode; header?: string; acceptLabel?: string; rejectLabel?: string; danger?: boolean; icon?: string }
interface ConfirmState { pendiente: { opts: ConfirmOpts; resolve: (v: boolean) => void } | null; pedir: (opts: ConfirmOpts) => Promise<boolean>; resolver: (v: boolean) => void }
const useConfirmStore = create<ConfirmState>((set, get) => ({
  pendiente: null,
  pedir: (opts) => new Promise((resolve) => { get().pendiente?.resolve(false); set({ pendiente: { opts, resolve } }); }),
  resolver: (v) => { const p = get().pendiente; set({ pendiente: null }); p?.resolve(v); },
}));

/** Mount once per admin page tree: renders the confirmation dialog host (los avisos los monta ProveedorUi). */
export function AdminHost() {
  const pendiente = useConfirmStore((s) => s.pendiente);
  const resolver = useConfirmStore((s) => s.resolver);
  const o = pendiente?.opts;
  return (
    <ConfirmarDialogo abierto={!!pendiente} onAbiertoChange={(a) => { if (!a) resolver(false); }} titulo={o?.header ?? 'Confirmar'} descripcion={o?.message}
      textoConfirmar={o?.acceptLabel ?? 'Confirmar'} textoCancelar={o?.rejectLabel ?? 'Cancelar'} destructivo={o?.danger} onConfirmar={() => resolver(true)} />
  );
}

/** Contextual confirmation. Resolves true when accepted. */
export function confirm(opts: ConfirmOpts): Promise<boolean> {
  return useConfirmStore.getState().pedir(opts);
}

/* ---------- Form primitives ---------- */
export { Field } from '@/components/ui';

/** Conversión Date ↔ valor de `<Input type="datetime-local">` (hora local). */
export const aFechaLocal = (d: Date | null | undefined) => (d ? new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : '');
export const deFechaLocal = (s: string): Date | null => (s ? new Date(s) : null);

/** JSON editor shown only in advanced mode. Validates on each keystroke; commits only valid JSON. */
export function JsonEditor({ value, onChange, rows = 8, id, validate }: { value: unknown; onChange: (v: unknown) => void; rows?: number; id?: string; validate?: (v: unknown) => string | null }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [err, setErr] = useState<string | null>(null);
  const last = useRef(text);
  useEffect(() => { const s = JSON.stringify(value ?? {}, null, 2); if (s !== last.current) { last.current = s; setText(s); setErr(null); } }, [value]);
  return (
    <div className="flex flex-col gap-1">
      <Textarea id={id} value={text} rows={rows} className="text-xs w-full" aria-invalid={!!err} aria-describedby={err ? `${id}-err` : undefined}
        onChange={(e) => {
          setText(e.target.value);
          try { const parsed = JSON.parse(e.target.value) as unknown; const v = validate?.(parsed) ?? null; setErr(v); if (!v) { last.current = JSON.stringify(parsed, null, 2); onChange(parsed); } }
          catch (ex) { setErr(ex instanceof Error ? ex.message : 'JSON inválido'); }
        }} />
      {err && <div id={`${id}-err`} className="text-xs text-destructive" role="alert">{err}</div>}
    </div>
  );
}

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (<div className="mb-2"><h3 className="text-sm font-semibold text-foreground m-0">{children}</h3>{hint && <p className="text-xs text-muted-foreground m-0 mt-0.5">{hint}</p>}</div>);
}

export function Help({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground m-0 flex items-start gap-1"><Icono nombre="mdi:information-outline" className="mt-0.5 shrink-0" aria-hidden /><span>{children}</span></p>;
}

/* ---------- Servicio externo ya configurado ---------- */
/**
 * Resumen de un servicio externo (buzón, correo saliente, bucket, TSA) ya configurado: qué hay conectado y los
 * datos clave, sin el formulario; este se abre con «Editar». `children`: estado y avisos; `actions`: la fila de botones.
 */
export function ConfiguredService({ icon, title, items, children, actions }: { icon: `${string}:${string}`; title: ReactNode; items: Array<{ label: string; value: ReactNode }>; children?: ReactNode; actions: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-md border border-border p-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-success/10 text-success" aria-hidden><Icono nombre={icon} className="text-xl" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><span className="font-medium break-words">{title}</span><Badge variant="success-light" size="sm"><Icono nombre="mdi:check-circle" /> Configurado</Badge></div>
          <div className="mt-2 text-sm"><KeyValue items={items} /></div>
        </div>
      </div>
      {children}
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

/* ---------- Data helpers ---------- */
export interface UnitNode { id: string; parentId: string | null; code: string; name: string; path: string; depth: number; active: boolean; managerUserId: string | null; description: string | null; children: UnitNode[] }
export function flattenUnits(tree: UnitNode[]): Array<{ id: string; name: string; label: string; depth: number; code: string; parentId: string | null; active: boolean }> {
  const out: Array<{ id: string; name: string; label: string; depth: number; code: string; parentId: string | null; active: boolean }> = [];
  const walk = (n: UnitNode, depth: number) => { out.push({ id: n.id, name: n.name, label: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${n.name}`, depth, code: n.code, parentId: n.parentId, active: n.active }); n.children.forEach((c) => walk(c, depth + 1)); };
  tree.forEach((n) => walk(n, 0));
  return out;
}

export const AVAILABILITY_LABEL: Record<string, string> = { AVAILABLE: 'Disponible', UNAVAILABLE: 'No disponible', VACATION: 'Vacaciones', LEAVE: 'Licencia', OFF_SHIFT: 'Fuera de turno', BLOCKED: 'Bloqueado' };
/** Permisos generales (lo que se haga con cada caso sale de la tarea y del organigrama, no de aquí). */
export const PERMISSION_LABEL: Record<string, string> = {
  'case.read': 'Ver expedientes de su área', 'case.read.all': 'Ver expedientes de toda la entidad', 'case.create': 'Radicar solicitudes',
  'reports.read': 'Ver reportes', 'emails.failed': 'Ver correos no enviados', 'audit.read': 'Ver auditoría', 'templates.manage': 'Administrar plantillas', 'admin.manage': 'Administrar configuración', 'admin.advanced': 'Administrar configuración avanzada',
};
/** Nombre interno de un campo de formulario (clave de los datos, no un «Código»): «Fecha del hecho» → «fechaDelHecho». */
export const camelKey = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-([a-z0-9])/g, (_, c: string) => c.toUpperCase());
export const pct = (v: number | null | undefined, digits = 0) => (v === null || v === undefined || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(digits)}%`);
export const hours = (h: number | null | undefined) => {
  if (h === null || h === undefined || Number.isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

/** Selección múltiple de tipos de caso (por código), sobre `SelectorMultiple`. */
export function CaseTypePicker({ id, value, options, onChange }: { id?: string; value: string[]; options: Array<{ code: string; name: string }>; onChange: (v: string[]) => void }) {
  return (
    <SelectorMultiple id={id} value={value} options={options.map((o) => ({ value: o.code, label: o.name }))} onChange={onChange}
      placeholder="Selecciona tipos de caso" placeholderBusqueda="Buscar tipo…" buscable />
  );
}
